import React, { PropsWithChildren } from 'react';
import { makeStyles } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import CategoryIcon from '@material-ui/icons/Category';
import ExtensionIcon from '@material-ui/icons/Extension';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import BarChartIcon from '@material-ui/icons/BarChart';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import GroupIcon from '@material-ui/icons/People';
import ExploreIcon from '@material-ui/icons/Explore';
import { Administration } from '@backstage-community/plugin-rbac';
import {
  useSidebarAccessConfig,
  useUserIdentity,
  hasAccess,
} from 'backstage-plugin-rbac-sidebar';
import { useAnalyticsTracking } from '@internal/backstage-plugin-analytics';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => {
  // Use analytics tracking hook
  useAnalyticsTracking();
  
  // Use hooks from the RBAC Sidebar plugin
  const accessConfig = useSidebarAccessConfig();
  const userIdentity = useUserIdentity();
  const userOwnership = userIdentity?.ownershipEntityRefs || [];

  return (
    <SidebarPage>
      <Sidebar>
        <SidebarLogo />
        <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
          <SidebarSearchModal />
        </SidebarGroup>
        <SidebarDivider />
        <SidebarGroup label="Menu" icon={<MenuIcon />}>
          {/* Global nav, not org-specific */}
          {hasAccess(userOwnership, accessConfig.home) && (
            <SidebarItem icon={HomeIcon} to="/" text="Home" />
          )}
          {hasAccess(userOwnership, accessConfig.catalog) && (
            <SidebarItem icon={CategoryIcon} to="catalog" text="Catalog" />
          )}
          {hasAccess(userOwnership, accessConfig.groups) && (
            <MyGroupsSidebarItem
              singularTitle="My Group"
              pluralTitle="My Groups"
              icon={GroupIcon}
            />
          )}
          {hasAccess(userOwnership, accessConfig.apis) && (
            <SidebarItem icon={ExtensionIcon} to="api-docs" text="APIs" />
          )}
          {hasAccess(userOwnership, accessConfig.docs) && (
            <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
          )}
          {hasAccess(userOwnership, accessConfig.create) && (
            <SidebarItem icon={CreateComponentIcon} to="create" text="Create..." />
          )}
          {hasAccess(userOwnership, accessConfig.techRadar) && (
            <SidebarItem icon={ExploreIcon} to="tech-radar" text="Tech Radar" />
          )}
          {hasAccess(userOwnership, accessConfig.analytics) && (
            <SidebarItem icon={BarChartIcon} to="analytics" text="Analytics" />
          )}
          {/* End global nav */}
          <SidebarDivider />
          <SidebarScrollWrapper>
            {/* Items in this group will be scrollable if they run out of space */}
          </SidebarScrollWrapper>
        </SidebarGroup>
        <SidebarSpace />
        <SidebarDivider />
        <SidebarGroup
          label="Settings"
          icon={<UserSettingsSignInAvatar />}
          to="/settings"
        >
          <SidebarSettings />
        </SidebarGroup>
        {hasAccess(userOwnership, accessConfig.administration) && (
          <Administration />
        )}
      </Sidebar>
      {children}
    </SidebarPage>
  );
};
